/**
 * "Do this to my cell" as a one-way request, the same shape as `settings-request.tsx`.
 *
 * Three tier-2 types have an affordance in the cell that is **not** a value write, because
 * monday's own API refuses a value write for them:
 *
 *  - `button` — clicking it fires the automation trigger `button_clicked` and bumps a counter
 *    (*"button state is managed by automations, not user data"* `[09§A.7 button]`);
 *  - `time_tracking` — *"The timer can only be started, stopped, and managed through the
 *    monday.com UI or automations"* `[09§A.7]`;
 *  - `file` — *"Files can only be added through the `add_file_to_column` mutation"*.
 *
 * A cell editor is rendered by the registry and is handed no store (CLAUDE.md rule 6), so the
 * request travels as a `CustomEvent` on `window`: the type folder dispatches it, the board
 * screen listens and calls the service. No import crosses in either direction.
 *
 * **P-128.** The listener is `TableView`'s `useCellActions` — for three rounds the dispatcher
 * existed and nothing received it, so the button, the timer and the file uploader were dead
 * ends. The mapping is one procedure per kind: `cells.clickButton`, `cells.timer.start` /
 * `.stop` / `.addSession`, and the file route (`add_file_to_column`).
 */
export const CELL_ACTION_EVENT = 'elizmonday:cell-action';

export type CellActionKind = 'button-click' | 'timer-start' | 'timer-stop' | 'timer-log' | 'file-upload';

export interface CellActionRequest {
  readonly kind: CellActionKind;
  readonly columnId: string;
  readonly itemId: string;
  /** `file-upload`: the file the cell's uploader chose. */
  readonly file?: File;
  /**
   * `timer-log`: the **manually entered** session. monday records a manual entry with all four
   * `manually_entered_*` flags, which is what `cells.timer.addSession` writes (M2 tier-2 §5.7).
   */
  readonly session?: { readonly startedAt: string; readonly endedAt: string };
}

export function requestCellAction(
  kind: CellActionKind,
  columnId: string,
  itemId: string,
  extra: Omit<CellActionRequest, 'kind' | 'columnId' | 'itemId'> = {},
): void {
  window.dispatchEvent(
    new CustomEvent<CellActionRequest>(CELL_ACTION_EVENT, {
      detail: { kind, columnId, itemId, ...extra },
    }),
  );
}

/** Subscribes to the request; returns the unsubscribe. */
export function onCellActionRequested(listener: (request: CellActionRequest) => void): () => void {
  const handler = (event: Event): void => {
    const detail = (event as CustomEvent<CellActionRequest>).detail;
    if (detail?.columnId && detail.itemId) listener(detail);
  };
  window.addEventListener(CELL_ACTION_EVENT, handler);
  return () => window.removeEventListener(CELL_ACTION_EVENT, handler);
}
